import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

const ABILITIES = ["str", "dex", "con", "int", "wis", "cha"];

function AbilityMultiPicker({ value, onChange, max, testId }) {
  const toggle = (a) => {
    if (value.includes(a)) onChange(value.filter((x) => x !== a));
    else if (!max || value.length < max) onChange([...value, a]);
  };
  return (
    <div className="flex flex-wrap gap-1" data-testid={testId}>
      {ABILITIES.map((a) => (
        <button
          key={a}
          type="button"
          data-testid={`${testId}-${a}`}
          onClick={() => toggle(a)}
          className={`text-[10px] uppercase px-2 py-1 rounded-full border transition-colors ${value.includes(a) ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "bg-black/30 text-[#f1e0c5]/60 border-[#4a3424]/50 hover:border-[#D4AF37]/50"}`}
        >
          {a}
        </button>
      ))}
    </div>
  );
}

const emptyAncestry = { name: "", ability_boosts: [], ability_flaw: [], free_boosts: 1, hp: 8, size: "medium", speed: 25, senses: "", traits_text: "", description: "" };
const emptyClass = { name: "", key_ability: [], hp_per_level: 8, description: "" };

/**
 * GM authoring tool for a Homebrew table's own ancestries & classes — same shape as the built-in
 * PF2e compendium (ability_boosts/flaw, free_boosts, senses, key_ability, hp, description) so the
 * exact same guided AbilityBoostWizardPf2e can drive character creation off them, no separate
 * wizard needed. Saved per-game and reusable across every character at the table.
 */
export default function HomebrewAncestryClassManager({ gameId }) {
  const [ancestries, setAncestries] = useState([]);
  const [classes, setClasses] = useState([]);
  const [newAncestry, setNewAncestry] = useState(emptyAncestry);
  const [newClass, setNewClass] = useState(emptyClass);
  const [creatingAncestry, setCreatingAncestry] = useState(false);
  const [creatingClass, setCreatingClass] = useState(false);

  const load = () => {
    api.get(`/games/${gameId}/homebrew/ancestries`).then((r) => setAncestries(r.data));
    api.get(`/games/${gameId}/homebrew/classes`).then((r) => setClasses(r.data));
  };
  useEffect(load, [gameId]);

  const saveAncestry = async () => {
    if (!newAncestry.name.trim()) return toast.error("Give it a name first");
    try {
      await api.post(`/games/${gameId}/homebrew/ancestries`, {
        ...newAncestry,
        senses: newAncestry.senses ? newAncestry.senses.split(",").map((s) => s.trim()).filter(Boolean) : [],
      });
      setNewAncestry(emptyAncestry);
      setCreatingAncestry(false);
      load();
      toast.success("Homebrew ancestry saved");
    } catch {
      toast.error("Could not save ancestry");
    }
  };

  const deleteAncestry = async (id) => {
    await api.delete(`/games/${gameId}/homebrew/ancestries/${id}`);
    load();
  };

  const saveClass = async () => {
    if (!newClass.name.trim()) return toast.error("Give it a name first");
    try {
      await api.post(`/games/${gameId}/homebrew/classes`, newClass);
      setNewClass(emptyClass);
      setCreatingClass(false);
      load();
      toast.success("Homebrew class saved");
    } catch {
      toast.error("Could not save class");
    }
  };

  const deleteClass = async (id) => {
    await api.delete(`/games/${gameId}/homebrew/classes/${id}`);
    load();
  };

  return (
    <div className="space-y-4" data-testid="homebrew-ancestry-class-manager">
      <div className="space-y-2">
        <p className="text-[10px] uppercase text-[#f1e0c5]/40">Custom Ancestries</p>
        {ancestries.map((a) => (
          <div key={a.id} data-testid={`homebrew-ancestry-row-${a.id}`} className="flex items-center justify-between bg-black/20 border border-white/10 rounded-lg px-3 py-1.5 text-xs">
            <span>{a.name} <span className="text-[#f1e0c5]/30">({(a.ability_boosts || []).join("/") || "no fixed boosts"}, +{a.free_boosts} free)</span></span>
            <button data-testid={`homebrew-ancestry-delete-${a.id}`} onClick={() => deleteAncestry(a.id)} className="text-[#f1e0c5]/40 hover:text-[#D90429]"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
        {!creatingAncestry ? (
          <Button data-testid="homebrew-ancestry-add-toggle" size="sm" variant="secondary" className="w-full rounded-full gap-1.5" onClick={() => setCreatingAncestry(true)}>
            <Plus className="w-3.5 h-3.5" /> New Custom Ancestry
          </Button>
        ) : (
          <div className="bg-black/20 border border-white/10 rounded-lg p-2.5 space-y-2">
            <Input data-testid="homebrew-ancestry-name" placeholder="Name (e.g. Stormkin)" value={newAncestry.name} onChange={(e) => setNewAncestry({ ...newAncestry, name: e.target.value })} className="bg-black/30 border-white/10 h-8 text-xs" />
            <div>
              <p className="text-[9px] uppercase text-[#f1e0c5]/40 mb-1">Fixed Boosts</p>
              <AbilityMultiPicker testId="homebrew-ancestry-boosts" value={newAncestry.ability_boosts} onChange={(v) => setNewAncestry({ ...newAncestry, ability_boosts: v })} />
            </div>
            <div>
              <p className="text-[9px] uppercase text-[#f1e0c5]/40 mb-1">Flaw</p>
              <AbilityMultiPicker testId="homebrew-ancestry-flaw" value={newAncestry.ability_flaw} onChange={(v) => setNewAncestry({ ...newAncestry, ability_flaw: v })} max={1} />
            </div>
            <div className="grid grid-cols-3 gap-1.5">
              <Input data-testid="homebrew-ancestry-free-boosts" type="number" min={0} value={newAncestry.free_boosts} onChange={(e) => setNewAncestry({ ...newAncestry, free_boosts: parseInt(e.target.value, 10) || 0 })} placeholder="Free boosts" className="bg-black/30 border-white/10 h-8 text-xs" title="Free boosts" />
              <Input data-testid="homebrew-ancestry-speed" type="number" value={newAncestry.speed} onChange={(e) => setNewAncestry({ ...newAncestry, speed: parseInt(e.target.value, 10) || 0 })} placeholder="Speed ft" className="bg-black/30 border-white/10 h-8 text-xs" title="Speed (ft)" />
              <Input data-testid="homebrew-ancestry-hp" type="number" value={newAncestry.hp} onChange={(e) => setNewAncestry({ ...newAncestry, hp: parseInt(e.target.value, 10) || 0 })} placeholder="Ancestry HP" className="bg-black/30 border-white/10 h-8 text-xs" title="Ancestry HP" />
            </div>
            <Input data-testid="homebrew-ancestry-senses" placeholder="Senses, comma separated (darkvision, low-light vision)" value={newAncestry.senses} onChange={(e) => setNewAncestry({ ...newAncestry, senses: e.target.value })} className="bg-black/30 border-white/10 h-8 text-xs" />
            <Textarea data-testid="homebrew-ancestry-description" placeholder="Description / traits blurb (shown on the sheet)" value={newAncestry.description} onChange={(e) => setNewAncestry({ ...newAncestry, description: e.target.value })} className="bg-black/30 border-white/10 text-xs min-h-16" />
            <div className="flex gap-1.5">
              <Button data-testid="homebrew-ancestry-save" size="sm" className="flex-1 rounded-full bg-[#D90429] hover:bg-[#b8031f]" onClick={saveAncestry}>Save Ancestry</Button>
              <Button data-testid="homebrew-ancestry-cancel" size="sm" variant="secondary" className="rounded-full" onClick={() => { setCreatingAncestry(false); setNewAncestry(emptyAncestry); }}>Cancel</Button>
            </div>
          </div>
        )}
      </div>

      <div className="space-y-2">
        <p className="text-[10px] uppercase text-[#f1e0c5]/40">Custom Classes</p>
        {classes.map((c) => (
          <div key={c.id} data-testid={`homebrew-class-row-${c.id}`} className="flex items-center justify-between bg-black/20 border border-white/10 rounded-lg px-3 py-1.5 text-xs">
            <span>{c.name} <span className="text-[#f1e0c5]/30">(key: {(c.key_ability || []).join("/") || "any"}, {c.hp_per_level} HP/lvl)</span></span>
            <button data-testid={`homebrew-class-delete-${c.id}`} onClick={() => deleteClass(c.id)} className="text-[#f1e0c5]/40 hover:text-[#D90429]"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
        {!creatingClass ? (
          <Button data-testid="homebrew-class-add-toggle" size="sm" variant="secondary" className="w-full rounded-full gap-1.5" onClick={() => setCreatingClass(true)}>
            <Plus className="w-3.5 h-3.5" /> New Custom Class
          </Button>
        ) : (
          <div className="bg-black/20 border border-white/10 rounded-lg p-2.5 space-y-2">
            <Input data-testid="homebrew-class-name" placeholder="Name (e.g. Runeblade)" value={newClass.name} onChange={(e) => setNewClass({ ...newClass, name: e.target.value })} className="bg-black/30 border-white/10 h-8 text-xs" />
            <div>
              <p className="text-[9px] uppercase text-[#f1e0c5]/40 mb-1">Key Ability (players pick one of these for the class boost)</p>
              <AbilityMultiPicker testId="homebrew-class-key-ability" value={newClass.key_ability} onChange={(v) => setNewClass({ ...newClass, key_ability: v })} />
            </div>
            <Input data-testid="homebrew-class-hp" type="number" value={newClass.hp_per_level} onChange={(e) => setNewClass({ ...newClass, hp_per_level: parseInt(e.target.value, 10) || 0 })} placeholder="HP per level" className="bg-black/30 border-white/10 h-8 text-xs" />
            <Textarea data-testid="homebrew-class-description" placeholder="Description / traits blurb (shown on the sheet)" value={newClass.description} onChange={(e) => setNewClass({ ...newClass, description: e.target.value })} className="bg-black/30 border-white/10 text-xs min-h-16" />
            <div className="flex gap-1.5">
              <Button data-testid="homebrew-class-save" size="sm" className="flex-1 rounded-full bg-[#D90429] hover:bg-[#b8031f]" onClick={saveClass}>Save Class</Button>
              <Button data-testid="homebrew-class-cancel" size="sm" variant="secondary" className="rounded-full" onClick={() => { setCreatingClass(false); setNewClass(emptyClass); }}>Cancel</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
